import React from "react";
import { Link } from "react-router-dom";
import {
  Video,
  Clock,
  Film,
  Sparkles,
  Layers,
  Radio,
  Globe2,
  Activity,
  GraduationCap,
  Trophy,
  ArrowRight,
  ShieldCheck,
  Lock,
  FileCheck2,
  BadgeCheck,
} from "lucide-react";
import { useI18n } from "../../i18n";
import { Container, Section, SectionHead, Eyebrow, Card } from "../../components/ui/Primitives";
import { Button } from "../../components/ui/Button";
import { FeatureCard, CTASection, VideoTile, WhatsAppButton } from "../../components/marketing/Shared";

function HeroMockup() {
  const { t } = useI18n();
  return (
    <div className="relative">
      <div className="rounded-2xl border border-white/10 bg-neutral-900/80 p-3 shadow-2xl backdrop-blur">
        <div className="mb-2.5 flex items-center justify-between px-1">
          <span className="flex items-center gap-2 text-xs font-semibold text-white/80">
            <span className="h-2 w-2 animate-pulse rounded-full bg-brand" />
            {t("home.hero.livePanel")}
          </span>
          <span className="font-mono text-[10px] text-white/40">{t("home.hero.liveTiles")}</span>
        </div>
        <div className="grid grid-cols-4 gap-1.5">
          {Array.from({ length: 16 }).map((_, i) => (
            <VideoTile key={i} label={`s-${i}`} alert={i === 5 || i === 11} small />
          ))}
        </div>
        <div className="mt-2.5 flex items-center justify-between rounded-lg bg-black/40 px-3 py-2">
          <span className="font-mono text-[10px] text-amber-400">● ingest 742 Mbps</span>
          <span className="font-mono text-[10px] text-white/50">4 shards · 99.98%</span>
        </div>
      </div>
      <div className="absolute -bottom-4 -right-4 hidden rounded-xl border border-white/10 bg-neutral-900 px-3 py-2 shadow-xl sm:block">
        <span className="font-mono text-[10px] text-white/50">server-clock</span>
        <p className="font-mono text-sm font-bold text-white">2026-07-17 14:32:08.412Z</p>
      </div>
    </div>
  );
}

export default function Home() {
  const { t } = useI18n();

  const features = [
    { icon: Video, k: "capture" },
    { icon: Clock, k: "timestamp" },
    { icon: Film, k: "noreencode" },
    { icon: Sparkles, k: "ai" },
    { icon: Layers, k: "scale" },
    { icon: Radio, k: "live" },
    { icon: Globe2, k: "crossos" },
    { icon: Activity, k: "telemetry" },
  ];

  const steps = ["s1", "s2", "s3", "s4"];

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border bg-neutral-950 text-white">
        <div className="absolute inset-0 cyber-grid" />
        <div
          className="pointer-events-none absolute -top-40 right-0 h-[36rem] w-[36rem] rounded-full blur-3xl"
          style={{ background: "hsl(var(--brand) / 0.16)" }}
        />
        <Container className="relative grid items-center gap-12 py-16 lg:grid-cols-[1.05fr_0.95fr] lg:py-24">
          <div>
            <Eyebrow>{t("home.hero.eyebrow")}</Eyebrow>
            <h1 className="mt-5 text-4xl font-black leading-[1.05] text-white sm:text-5xl lg:text-6xl">
              {t("home.hero.title1")}
              <br />
              <span className="text-gradient-brand">{t("home.hero.titleHighlight")}</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/70">{t("home.hero.lead")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button to="/contato" size="lg">
                {t("home.hero.ctaDemo")}
              </Button>
              <WhatsAppButton variant="outline" className="border-white/25 text-white hover:bg-white/10" />
            </div>
          </div>
          <HeroMockup />
        </Container>
      </section>

      {/* Verticals */}
      <Section>
        <Container>
          <SectionHead
            center
            eyebrow={t("home.verticals.eyebrow")}
            title={t("home.verticals.title")}
            subtitle={t("home.verticals.subtitle")}
          />
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {[
              {
                to: "/exames",
                icon: GraduationCap,
                accent: "exams",
                tag: t("home.verticals.exams.tag"),
                title: t("home.verticals.exams.title"),
                desc: t("home.verticals.exams.desc"),
                cta: t("home.verticals.exams.cta"),
              },
              {
                to: "/ctf",
                icon: Trophy,
                accent: "ctf",
                tag: t("home.verticals.ctf.tag"),
                title: t("home.verticals.ctf.title"),
                desc: t("home.verticals.ctf.desc"),
                cta: t("home.verticals.ctf.cta"),
              },
            ].map((v) => (
              <Link
                key={v.to}
                to={v.to}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card p-8 transition-all hover:-translate-y-1 hover:shadow-lg"
                style={{ boxShadow: "inset 0 0 0 1px transparent" }}
              >
                <div
                  className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full blur-3xl transition-opacity group-hover:opacity-100"
                  style={{ background: `hsl(var(--${v.accent}) / 0.14)`, opacity: 0.6 }}
                />
                <div
                  className="relative inline-flex h-14 w-14 items-center justify-center rounded-2xl"
                  style={{ background: `hsl(var(--${v.accent}) / 0.14)`, color: `hsl(var(--${v.accent}))` }}
                >
                  <v.icon className="h-7 w-7" />
                </div>
                <p
                  className="relative mt-5 font-mono text-xs font-bold uppercase tracking-wider"
                  style={{ color: `hsl(var(--${v.accent}))` }}
                >
                  {v.tag}
                </p>
                <h3 className="relative mt-2 text-2xl">{v.title}</h3>
                <p className="relative mt-3 text-sm leading-relaxed text-muted-foreground">{v.desc}</p>
                <span
                  className="relative mt-6 inline-flex items-center gap-1.5 text-sm font-semibold"
                  style={{ color: `hsl(var(--${v.accent}))` }}
                >
                  {v.cta}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </Container>
      </Section>

      {/* Demo video */}
      <Section>
        <Container>
          <SectionHead
            center
            eyebrow={t("home.video.eyebrow")}
            title={t("home.video.title")}
            subtitle={t("home.video.subtitle")}
          />
          <div
            className="relative mx-auto mt-10 max-w-4xl overflow-hidden rounded-2xl border border-border bg-neutral-950 shadow-lg"
            style={{ aspectRatio: "16 / 9" }}
          >
            <iframe
              className="absolute inset-0 h-full w-full"
              src="https://www.youtube-nocookie.com/embed/fV1Mw7Wyizo"
              title="SecProctoring"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </Container>
      </Section>

      {/* Features */}
      <Section alt>
        <Container>
          <SectionHead
            center
            eyebrow={t("home.features.eyebrow")}
            title={t("home.features.title")}
            subtitle={t("home.features.subtitle")}
          />
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <FeatureCard
                key={f.k}
                icon={f.icon}
                title={t(`feat.${f.k}.title`)}
                desc={t(`feat.${f.k}.desc`)}
              />
            ))}
          </div>
          <div className="mt-8 text-center">
            <Button to="/recursos" variant="outline">
              {t("common.learnMore")} <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </Container>
      </Section>

      {/* How it works */}
      <Section alt>
        <Container>
          <SectionHead center eyebrow={t("home.how.eyebrow")} title={t("home.how.title")} />
          <div className="mt-12 grid gap-6 md:grid-cols-4">
            {steps.map((s, i) => (
              <div key={s} className="relative">
                <span className="font-mono text-5xl font-black text-brand/25">{i + 1}</span>
                <h3 className="mt-2 text-lg">{t(`home.how.${s}.title`)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t(`home.how.${s}.desc`)}</p>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Compliance */}
      <Section>
        <Container>
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <SectionHead
                eyebrow={t("home.compliance.eyebrow")}
                title={t("home.compliance.title")}
                subtitle={t("home.compliance.subtitle")}
              />
              <Button to="/seguranca" variant="outline" className="mt-6">
                {t("common.learnMore")} <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                { icon: ShieldCheck, label: t("home.compliance.lgpd") },
                { icon: BadgeCheck, label: t("home.compliance.soc2") },
                { icon: Lock, label: t("home.compliance.encryption") },
                { icon: FileCheck2, label: t("home.compliance.dpa") },
              ].map((c) => (
                <Card key={c.label} className="flex items-center gap-3 p-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-success/12 text-success">
                    <c.icon className="h-5 w-5" />
                  </span>
                  <span className="text-sm font-semibold">{c.label}</span>
                </Card>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      <CTASection />
    </>
  );
}
